import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import QuoteForm from "../components/QuoteForm";
import { useConfig } from "../context/ConfigContext";
import { useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Mail, Phone, MapPin, MessageCircle, Clock, ArrowRight } from "lucide-react";
import { submitContact } from "../lib/api";
import { toast } from "sonner";
import { FEATURED_HUBS, OTHER_HUBS } from "../data/hubs";

function ContactPageLegacy() {
  const [params] = useSearchParams();
  const isQuote = params.get("get-quote") === "1";
  const [tab, setTab] = useState(isQuote ? "quote" : "message");
  const { email, phone, phone_display, finance_email, address, whatsapp } = useConfig();
  const { t, lang } = useI18n();

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isQuote) return;
    // Phase 18.12 — scroll directly to the quote form so a CTA like
    // "Vraag offerte aan" lands the customer on the form itself,
    // never on the hubs/vestigingen section above. We retry briefly
    // because the cargo-info modal (BlockRenderer) can hijack the
    // initial scroll position on first render.
    let attempts = 0;
    const tryScroll = () => {
      const target = document.getElementById("quote-form") ||
                     document.getElementById("contact-tabs");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      attempts += 1;
      if (attempts < 4) setTimeout(tryScroll, 350);
    };
    // First attempt deferred so the page can paint + modal can settle.
    const t = setTimeout(tryScroll, 150);
    return () => clearTimeout(t);
  }, [isQuote]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.msg_required_error"));
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success(t("contact.msg_send_success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error(t("contact.msg_send_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={t("contact.seo_title")}
        description={t("contact.seo_description")}
        keywords={t("contact.seo_keywords")}
      />

      <section className="relative overflow-hidden bg-[#082c50] text-white" data-testid="contact-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#082c50] via-[#0a335c] to-[#194d8d]" />
          <div className="absolute -top-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-cyan-400/10 blur-[120px]" />
          <div className="absolute top-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-[#1a5193]/40 blur-[140px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[11px] tracking-[0.18em] uppercase font-chivo font-bold">
            {t("contact.eyebrow")}
          </span>
          <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04] mt-5">
            {t("contact.h1_a")} <br />
            <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-white bg-clip-text text-transparent">
              {t("contact.h1_b")}
            </span>
          </h1>
          <p className="mt-5 text-white/85 max-w-2xl text-lg leading-relaxed">{t("contact.intro")}</p>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="contact-info-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <a
            href={`https://wa.me/${whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#25D366] text-white p-6 hover:bg-[#1fb858] transition-colors"
            data-testid="contact-whatsapp-card"
          >
            <MessageCircle className="w-7 h-7 mb-3" />
            <div className="text-xs tracking-[0.2em] uppercase font-chivo font-bold opacity-80">
              {t("contact.card_whatsapp_label")}
            </div>
            <div className="font-chivo font-black text-xl mt-1">{phone_display || phone}</div>
          </a>
          <a
            href={`mailto:${email}`}
            className="bg-[#F8F9FA] border border-gray-200 p-6 hover:border-[#082c50] transition-colors"
            data-testid="contact-email-card"
          >
            <Mail className="w-7 h-7 mb-3 text-[#1a5193]" />
            <div className="text-xs tracking-[0.2em] uppercase font-chivo font-bold text-gray-500">
              {t("contact.card_email_label")}
            </div>
            <div className="font-chivo font-black text-xl mt-1 text-[#082c50] break-all">{email}</div>
          </a>
          <a
            href={`tel:${phone}`}
            className="bg-[#F8F9FA] border border-gray-200 p-6 hover:border-[#082c50] transition-colors"
            data-testid="contact-phone-card"
          >
            <Phone className="w-7 h-7 mb-3 text-[#1a5193]" />
            <div className="text-xs tracking-[0.2em] uppercase font-chivo font-bold text-gray-500">
              {t("contact.card_phone_label")}
            </div>
            <div className="font-chivo font-black text-xl mt-1 text-[#082c50]">{phone_display || phone}</div>
          </a>
          <div className="bg-[#F8F9FA] border border-gray-200 p-6" data-testid="contact-hours-card">
            <Clock className="w-7 h-7 mb-3 text-[#1a5193]" />
            <div className="text-xs tracking-[0.2em] uppercase font-chivo font-bold text-gray-500">
              {t("contact.card_hours_label")}
            </div>
            <div className="font-chivo font-black text-base mt-1 text-[#082c50] leading-tight">
              {t("contact.card_hours_value_line1")}<br />
              {t("contact.card_hours_value_line2")}<br />
              <span className="font-normal text-gray-500 text-sm">
                {t("contact.card_hours_value_zone")}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section id="contact-hubs" className="py-16 bg-white border-t border-gray-200" data-testid="contact-hubs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.2em] uppercase text-[#1a5193] font-chivo font-bold">
              {t("contact_hubs.eyebrow")}
            </span>
            <h2 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] tracking-tight mt-3">
              {t("contact_hubs.title")}
            </h2>
            <p className="mt-4 text-gray-600 leading-relaxed">
              {t("contact_hubs.sub")}
            </p>
          </div>

          <h3 className="font-chivo font-black text-base text-[#082c50] tracking-[0.2em] uppercase mt-10 mb-4">
            {t("contact_hubs.main_label")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {FEATURED_HUBS.map((hub) => {
              const countryName = lang === "nl" ? hub.country_nl : hub.country;
              const hubLabel = lang === "nl" ? hub.label_nl : hub.label_en;
              return (
                <div
                  key={hub.key}
                  data-testid={`contact-hub-main-${hub.key}`}
                  className="bg-white border-2 border-[#082c50] p-6 flex flex-col"
                >
                  <span className="inline-block bg-[#1a5193] text-white text-[10px] tracking-[0.2em] uppercase font-chivo font-bold px-2 py-1 mb-3 self-start">
                    {hubLabel}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl" aria-hidden>{hub.flag}</span>
                    <h4 className="font-chivo font-black text-xl text-[#082c50]">{countryName}</h4>
                  </div>
                  <div className="mt-3 text-sm text-gray-700 space-y-2 flex-1">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
                      <div>
                        {hub.address.map((line, i) => (<div key={i}>{line}</div>))}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Phone className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
                      <div>
                        {hub.phones.map((p, i) => (
                          <a key={i} href={`tel:${p.replace(/\s+/g, "")}`} className="block hover:text-[#1a5193]">{p}</a>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
                      <a href={`mailto:${hub.emails[0]}`} className="break-all hover:text-[#1a5193]">{hub.emails[0]}</a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <h3 className="font-chivo font-black text-base text-[#082c50] tracking-[0.2em] uppercase mt-12 mb-4">
            {t("contact_hubs.other_label")}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
            {OTHER_HUBS.map((hub) => {
              const countryName = lang === "nl" ? hub.country_nl : hub.country;
              return (
                <Link
                  key={hub.key}
                  to={`/hubs#${hub.key}`}
                  data-testid={`contact-hub-other-${hub.key}`}
                  className="border border-gray-200 bg-[#F8F9FA] p-4 flex items-center gap-3 hover:border-[#082c50] transition-colors"
                >
                  <span className="text-xl" aria-hidden>{hub.flag}</span>
                  <span className="font-chivo font-bold text-sm text-[#082c50]">{countryName}</span>
                </Link>
              );
            })}
          </div>

          <div className="mt-10">
            <Link
              to="/hubs"
              data-testid="contact-view-all-hubs"
              className="inline-flex items-center bg-[#082c50] hover:bg-[#082c50] text-white font-chivo font-bold tracking-wide uppercase text-xs px-5 py-3"
            >
              {t("contact_hubs.view_all")} <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <section id="contact-tabs" className="py-12 bg-[#F8F9FA] border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="flex gap-2 mb-6" data-testid="contact-tabs">
              <button
                onClick={() => setTab("quote")}
                data-testid="tab-quote"
                className={`px-5 py-2 font-chivo font-bold tracking-wide uppercase text-xs border ${
                  tab === "quote"
                    ? "bg-[#082c50] text-white border-[#082c50]"
                    : "bg-white text-[#082c50] border-gray-300"
                }`}
              >
                {t("contact.tab_quote")}
              </button>
              <button
                onClick={() => setTab("message")}
                data-testid="tab-message"
                className={`px-5 py-2 font-chivo font-bold tracking-wide uppercase text-xs border ${
                  tab === "message"
                    ? "bg-[#082c50] text-white border-[#082c50]"
                    : "bg-white text-[#082c50] border-gray-300"
                }`}
              >
                {t("contact.tab_message")}
              </button>
            </div>

            {tab === "quote" ? (
              <div id="quote-form" data-testid="quote-form-anchor">
                <QuoteForm />
              </div>
            ) : (
              <form onSubmit={onSubmit} className="bg-white border border-gray-200 p-8 space-y-4" data-testid="contact-form">
                <h3 className="font-chivo font-black text-2xl text-[#082c50]">{t("contact.msg_title")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs tracking-wide uppercase font-chivo font-bold">
                      {t("contact.msg_name")}
                    </Label>
                    <Input
                      data-testid="contact-input-name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 rounded-xl border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-wide uppercase font-chivo font-bold">
                      {t("contact.msg_email")}
                    </Label>
                    <Input
                      data-testid="contact-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1 rounded-xl border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs tracking-wide uppercase font-chivo font-bold">
                      {t("contact.msg_phone")}
                    </Label>
                    <Input
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-1 rounded-xl border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-wide uppercase font-chivo font-bold">
                      {t("contact.msg_subject")}
                    </Label>
                    <Input
                      data-testid="contact-input-subject"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="mt-1 rounded-xl border-gray-300"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs tracking-wide uppercase font-chivo font-bold">
                    {t("contact.msg_message")}
                  </Label>
                  <Textarea
                    data-testid="contact-input-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1 rounded-xl border-gray-300"
                  />
                </div>
                <Button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-sm py-6"
                >
                  {loading ? t("contact.msg_sending") : t("contact.msg_submit")}
                </Button>
              </form>
            )}
          </div>

          <div>
            <h3 className="font-chivo font-black text-2xl text-[#082c50] mb-4">{t("contact.visit_title")}</h3>
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#1a5193] mt-1" />
                <div>
                  <div className="font-chivo font-bold text-[#082c50]">{t("contact.company_name")}</div>
                  <div className="text-sm text-gray-600 mt-1" data-testid="contact-address">
                    {address}
                  </div>
                </div>
              </div>
              <div className="mt-6 aspect-[4/3] w-full overflow-hidden border border-gray-200">
                <iframe
                  title="5999Cargo Rotterdam location"
                  src="https://www.google.com/maps?q=Steenhouwerstraat+88,+3194+AG+Rotterdam&output=embed"
                  className="w-full h-full"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
              <div className="mt-6">
                <h4 className="font-chivo font-bold text-[#082c50]">{t("contact.dropoff_title")}</h4>
                <p className="text-sm text-gray-600 mt-2">{t("contact.dropoff_desc")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}


import CmsPageRenderer from "../components/CmsPageRenderer";
import ContactFormSection from "../components/ContactFormSection";
import ContactPageBanner from "../components/ContactPageBanner";
import RelatedDestinations from "../components/RelatedDestinations";

export default function ContactPage() {
  return (
    <CmsPageRenderer
      slug="contact"
      testid="contact-cms-driven"
      hideBlockTypes={["hero"]}
      topSection={<ContactPageBanner />}
      fallback={<ContactPageLegacy />}
      extraSections={
        <>
          <ContactFormSection />
          <RelatedDestinations
            testid="contact-related"
            eyebrow="Verzending per bestemming"
            title="Waar naartoe verzendt u?"
            description="Bekijk per bestemming wat wij verzorgen — en vraag een gerichte offerte aan."
            anchorTemplate="Offerte voor {name}"
          />
        </>
      }
    />
  );
}
